import { createReducer } from 'typesafe-actions'
import { Project, ProjectRecord } from '../../../responses/projects'
import { ID } from '../../../types/UtilityTypes'
import { normalizeProject, normalizeProjects } from '../../../utils/normalize'
import { combineReducers } from 'redux'
import { taskListActions } from '../../pages/task-list/task-list-actions'
import { departmentsProjects, projectActions } from './project-actions'
import { taskActions } from '../task/task-actions'
import { modifyProp, snoc } from '../../../utils/utils'
import { projectDetailActions } from '../../pages/project-detail'
import { existLens, unsubscribedLens } from '../../../utils/lenses'
import { idRemove } from '../../../utils/entity-utils'
import { RequestStatus } from '../../../responses/response'
import { statusReducer } from '../../../utils/redux-utils'
import { archiveActions } from '../../pages/archive'

import { ADTType, makeADT, ofType } from '@morphic-ts/adt'

export const ProjectEditState = makeADT('type')({
    none: ofType<{type: 'none'}>(),
    close: ofType<{type: 'close', project: Project}>(),
    delete: ofType<{type: 'delete', project: Project}>(),
    cancel: ofType<{type: 'cancel', project: Project}>(),
    bind: ofType<{type: 'bind', project: Project}>(),
})

export interface ProjectState {
    editState: ADTType<typeof ProjectEditState>,
    updateStatus: RequestStatus,
    closeStatus: RequestStatus,
    byId: ProjectRecord,
}

export const projectReducer = combineReducers<ProjectState>({
    editState: createReducer<ADTType<typeof ProjectEditState>>(ProjectEditState.as.none({}))
        .handleAction(projectActions.startCancel, (_, action) => ProjectEditState.as.cancel({project: action.payload}))
        .handleAction(projectActions.startDelete, (_, action) => ProjectEditState.as.delete({project: action.payload}))
        .handleAction(projectActions.startBind, (_, action) => ProjectEditState.as.bind({project: action.payload}))
        .handleAction(projectActions.startClose, (_, action) => ProjectEditState.as.close({project: action.payload}))
        .handleAction([projectActions.cancelEdit, projectActions.close.success], () => ProjectEditState.as.none({}))
        .handleAction(projectActions.update.success, (state, action) => action.meta === 'user'? ProjectEditState.as.none({}): state),
    updateStatus: statusReducer(projectActions.update),
    closeStatus: statusReducer(projectActions.close),
    byId: createReducer<ProjectRecord>({})
        .handleAction([
            projectActions.get.success,
            projectActions.close.success,
            projectActions.create.success,
            projectActions.update.success,
        ], (state, action) => modifyProp<ProjectRecord, ID>(action.payload.id, normalizeProject(action.payload))(state))
        .handleAction(projectActions.reservable.success, (state, action) => normalizeProjects(action.payload.rows)(state))
        .handleAction(projectActions.checkSource.success, (state, action) => modifyProp<ProjectRecord, ID>(action.payload, existLens.set(1))(state))
        .handleAction([archiveActions.archive.success, archiveActions.unarchive.success, archiveActions.reserve.success],
            (state, action) => normalizeProjects(action.payload)(state)
        )
        .handleAction([projectActions.list.success, projectActions.archive.success], (state, action) =>
            normalizeProjects(action.payload.projects)(state))
        .handleAction(taskActions.get.success, (state, action) =>
            modifyProp<ProjectRecord, ID>(action.payload.project.id, normalizeProject(action.payload.project))(state))
        .handleAction(taskActions.requested.success, (state, action) =>
            normalizeProjects(action.payload.map(task => task.project))(state))
        .handleAction(taskActions.user.success, (state, action) =>
            normalizeProjects(action.payload.tasks.map(task => task.project))(state))
        .handleAction(taskListActions.departments.success, (state, action) =>
            normalizeProjects(departmentsProjects(action.payload))(state))
        .handleAction(projectDetailActions.unsubscribe.success, (state, action) =>
            modifyProp<ProjectRecord, ID>(action.payload.projectId, unsubscribedLens.modify(snoc(action.payload.userId)))(state))
        .handleAction(projectDetailActions.subscribe.success, (state, action) =>
            modifyProp<ProjectRecord, ID>(action.payload.projectId, unsubscribedLens.modify(idRemove(action.payload.userId)))(state)),
})

